import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, X, Loader2, Images } from "lucide-react";
import { toast } from "sonner";

function LibraryDialog({ open, onOpenChange, onPick }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = () => { setLoading(true); api.get("/media").then((r) => setMedia(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { if (open) load(); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-cream max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-xl text-bordeaux">Libreria immagini</DialogTitle></DialogHeader>
        {loading ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-brass" /></div> : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {media.map((m) => (
              <button key={m.id} type="button" onClick={() => { onPick(m.url); onOpenChange(false); }} data-testid={`lib-pick-${m.id}`}
                className="group relative rounded-lg overflow-hidden border border-brass/20 hover:border-brass aspect-square">
                <img src={`${fileUrl(m.url)}?v=${m.updated_at || ""}`} alt={m.alt || m.title} className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
            {media.length === 0 && <p className="col-span-full text-center text-oak/50 py-8 text-sm">Nessuna immagine in libreria. Carica la prima.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ImageUpload({ value, onChange, label = "Immagine", accept = "image/*", testid = "upload" }) {
  const ref = useRef();
  const [loading, setLoading] = useState(false);
  const [libOpen, setLibOpen] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.url);
      toast.success("File caricato e ottimizzato");
    } catch (err) {
      toast.error("Caricamento fallito");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-oak/70">{label}</label>
      <div className="flex items-start gap-3 mt-1">
        {value ? (
          <div className="relative">
            <img src={fileUrl(value)} alt="preview" className="w-20 h-20 object-cover rounded-lg border border-brass/30" />
            <button type="button" onClick={() => onChange("")} className="absolute -top-2 -right-2 bg-bordeaux text-cream rounded-full p-0.5" data-testid={`${testid}-remove`}><X className="w-3 h-3" /></button>
          </div>
        ) : (
          <div className="w-20 h-20 rounded-lg border border-dashed border-brass/40 flex items-center justify-center text-oak/40"><Upload className="w-5 h-5" /></div>
        )}
        <div className="flex-1">
          <input ref={ref} type="file" accept={accept} onChange={handleFile} className="hidden" data-testid={`${testid}-input`} />
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={loading} data-testid={`${testid}-btn`}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Carica file"}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setLibOpen(true)} data-testid={`${testid}-lib`}>
              <Images className="w-4 h-4 mr-1" />Libreria
            </Button>
          </div>
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="oppure incolla un URL https://…" className="mt-2 h-8 text-xs" data-testid={`${testid}-url`} />
        </div>
      </div>
      <LibraryDialog open={libOpen} onOpenChange={setLibOpen} onPick={onChange} />
    </div>
  );
}
